/**
 * The design tokens, lifted from the Figma file `k57NWaiDfZDWBH95l2B8KH`.
 *
 * **Raw values live here and nowhere else**, as plain objects rather than a
 * theme, so a test or a plain function can read one without constructing a
 * theme first. `theme.ts` is the consumer that matters.
 *
 * A component reads the *theme*, not this file — see
 * `.claude/rules/engineering/clients.md`.
 */

/**
 * A colour that differs between modes carries both; one that does not is a bare
 * string, and the bare string is the claim, not an omission.
 */
export type ModeValue = string | { light: string; dark: string };

export type ThemeMode = 'light' | 'dark';

/**
 * The raw paint, for values shared by **more than one semantic token for a stated
 * reason**. Private: nothing outside this file references it, and a component
 * reads the theme.
 *
 * **The bar is "same colour for the same reason", and most duplication in this
 * file fails it.** `#1f73c2` is `text.link`, `stage.build` and
 * `contentType.Method`; `#1a8573` is Discovery's accessible colour and
 * `contentType.Phase`. Those coincide today and their meanings never do — the
 * rules file is explicit that a content-type colour is never an alias of a stage
 * colour — so collapsing them onto one entry would tie two unrelated things
 * together and break both the day either moves. **They stay written out.**
 *
 * `#ffffff` is the same trap, and the one the dark palette settled: `text.onBrand`
 * is white in both modes because it sits on a stage fill, and `surface.page` and
 * `surface.raised` are white in light only — the dark values below are exactly
 * the divergence that trap predicted.
 */
const PALETTE = {
  /** AND Digital red. */
  brandRed: '#d82036',
  /** AND Digital orange. */
  brandOrange: '#ff7900',
} as const;

/**
 * Three stages whose Figma colour already meets AA on white, so their `fill` and
 * their light `onSurface` are **one value rather than two that happen to match**.
 * Named here so the pair cannot drift apart by an edit to one half.
 *
 * Discovery and Run are the two whose light pair genuinely differs; they are
 * written out below.
 */
const DELIVERY_MAGENTA = '#c93c9b';
const DESIGN_PURPLE = '#7d41c8';
const BUILD_BLUE = '#1f73c2';

/**
 * Text colours. **`onBrand` is white on a brand-coloured ground** — text on
 * `action.primary`, and the logo marks on an environment colour, which is why it
 * is not simply `#ffffff` at each call site.
 *
 * **`disabled` fails AA at 2.72:1 on a card, which is why `meta` (6.42:1 on a
 * card, 5.63:1 on the darkest tint) exists.** `disabled` is only for genuinely
 * inactive controls, which WCAG 1.4.3 exempts; anything carrying meaning takes
 * `meta`. `contrast.test.ts` recomputes both against every ground.
 */
export const text = {
  // Light values are the WCAG fix ported from main: `#323232`/`#757575`/`#6b6e73`
  // were the pre-fix greys, all a shade too light on `surface.raised`.
  primary: { light: '#17191c', dark: '#e8e9eb' },
  secondary: { light: '#4a4d52', dark: '#b4b8be' },
  // AA-exempt by design either way (WCAG 1.4.3, inactive controls only). One
  // value is deliberate, not an omission — forking it would imply a
  // dark-specific disabled treatment nobody asked for.
  disabled: '#9a9da2',
  meta: { light: '#5c5f63', dark: '#8b9098' },
  // Authored for dark rather than inherited from light: `#1f73c2` is ~3.3:1 on
  // the dark page, below AA for body text. Lightened until it clears 4.5:1 on
  // all three dark grounds, the worst case being sunken at 4.56:1.
  link: { light: '#1f73c2', dark: '#3e92e0' },
  onBrand: '#ffffff',
} as const satisfies Record<string, ModeValue>;

/** The one brand action colour. AND Digital red. */
export const action = {
  primary: PALETTE.brandRed,
} as const;

/**
 * Page grounds, in depth order rather than in lightness order — dark mode runs
 * the lightness the other way. `raised` is a card on the page; `sunken` is a
 * well *below* it — a filter panel, a skeleton block, a disabled field.
 *
 * **`page` and `raised` are both white in light**, the same trap `PALETTE` above
 * names: Figma draws the page and a card on the same `bg/surface` variable, so
 * a light card is set apart by its border and shadow alone — and dark is where
 * the two tokens actually separate (`#0f1115` vs `#1a1d23`).
 *
 * `raised` used to be `#fafafa`, an off-white distinct from the page's pure
 * white. That put body copy on `#fafafa` at 4.41:1 — below AA — because no grey
 * dark enough to fix that stayed distinguishable from `primary`. Matching
 * Figma's actual `bg/surface` fixes it: light `raised` is light `page` exactly.
 */
export const surface = {
  page: { light: '#ffffff', dark: '#0f1115' },
  raised: { light: '#ffffff', dark: '#1a1d23' },
  sunken: { light: '#f1f3f5', dark: '#23272e' },
} as const satisfies Record<string, ModeValue>;

/**
 * **In light, this border is a card's only edge** — page and card share the
 * same white ground, so it has to clear WCAG 1.4.11's 3:1 as a graphical object
 * in its own right. The values Figma drew, `#dee0e3`/`#c2c5c9` before this fix
 * and `#e3e5e8`/`#c2c5c9` as ported from the file, were 1.26:1 and 1.73:1 on
 * white — nowhere close, on every card, input and divider in the app.
 * `contrast.test.ts` asserts both against every ground, in both modes.
 *
 * `strong` is `default`'s hover state (`interactiveCardSx`). Light's value is
 * the light `text.meta` held before this same fix, reused rather than invented;
 * dark's reuses dark `text.meta` for the same reason.
 */
export const border = {
  default: { light: '#8a8d91', dark: '#6b7280' },
  strong: { light: '#6b6e73', dark: '#8b9098' },
} as const satisfies Record<string, ModeValue>;

/**
 * Tinted grounds, the guardrail treatment, and the focus ring.
 *
 * `blueSurface` is the mastery-target highlight on a method page — its label
 * and disc take `accent.info` rather than `text.link`, which is 4.30:1 there
 * and below AA; see `MasteryLevels`.
 *
 * `focusRing` is deliberately *not* `text.link`: a ring that matches link
 * colour is invisible on a focused link.
 *
 * **`brandSurface` is the error ground**, and only that — `ErrorState`'s icon
 * tile and the `SearchField` focus glow. Red is the action colour, so tinting
 * anything decorative with it would read as something to click. Its icon
 * takes `errorText`, not `action.primary`: the brand red is 4.31:1 on this
 * tint, below AA, and darkening the tint only pulls the pair closer together
 * rather than apart — the same lesson `contentType` already carries.
 * `errorText` is this file's own `state/error/text`, ported unchanged (it was
 * never wired to code): 5.33:1 on `brandSurface`, 6.22:1 on white.
 *
 * **The `info*` trio is the guardrail card, and it exists to stay off that
 * register.** A tinted guardrail card and `ErrorState` can appear on one method
 * page at once, so anything the guardrails share with the error ground makes a
 * load failure and the responsible-use rules read alike.
 *
 * **`infoSurface` is one hex from `blueSurface` in light and equal to it in dark,
 * and is not it.** A mastery highlight and a guardrail ground are different
 * claims, and the day either moves it moves alone — the same argument that keeps
 * `contentType` off the stage palette. `info` is likewise not `text.link`, which
 * on the light ground fails AA at 4.33:1 where `info` clears it at 4.63:1.
 *
 * **`infoBorder` is `info` itself, not a paler shade of it, in both modes.** The
 * paler shade light previously carried (`#9ebde5`) was 1.7:1 against
 * `infoSurface` and 1.9:1 against white — nowhere near the 3:1 WCAG 1.4.11
 * needs, and the guardrail card has no other boundary marker. `info` reads
 * 4.63:1 there, and a border matching the heading it outlines is the simpler
 * outcome, not just the accessible one.
 */
export const accent = {
  blueSurface: { light: '#e9f1fa', dark: '#16202e' },
  brandSurface: { light: '#fce9eb', dark: '#2a1518' },
  infoSurface: { light: '#eaf2fb', dark: '#16202e' },
  info: { light: '#1d6fb8', dark: '#6ba3e0' },
  infoBorder: { light: '#1d6fb8', dark: '#6ba3e0' },
  /**
   * `ErrorState`'s icon, in place of `action.primary` (4.31:1 on `brandSurface`,
   * below AA). 5.33:1 on light `brandSurface`, 6.22:1 on white.
   *
   * **Light only — dark has no vetted value yet.** `#c1121f` reads 2.77:1 on
   * dark `brandSurface` (`#2a1518`), below AA; it needs a Figma value rather
   * than an invented one, the same gap `elevation` has.
   */
  errorText: '#c1121f',
  /**
   * Mode-invariant, deliberately. No Figma variable in either mode — always a
   * hardcoded decision. Against the dark grounds it clears WCAG 1.4.11's 3:1
   * floor at 4.42:1 (page), 3.94:1 (raised), 3.50:1 (sunken) — thinner than
   * light's 4.28:1 margin but compliant. `contrast.test.ts` pins the floor so a
   * future dark-ground change that erodes it further gets caught.
   */
  focusRing: '#4078d9',
} as const satisfies Record<string, ModeValue>;

/**
 * The grounds behind an added and a removed line when two versions of something are
 * compared: pastel green and pastel red in light, and the same two hues deepened in
 * dark, where a pastel would be a bright slab on a dark page.
 *
 * **Their own pair, not `accent.brandSurface` and not a green borrowed from
 * anywhere.** `brandSurface` is the error ground and only that, so a removed line
 * drawn in it would read as something that had gone wrong rather than something that
 * had changed. The red here is a different value, in the same spirit as `infoSurface`
 * against `blueSurface`: two claims that look alike today and move apart separately.
 *
 * **Text takes `text.primary`, and the gutter sign `text.secondary`, on both.** Both
 * clear AA on both grounds in both modes — 13:1 and above for the text, 5.8:1 and
 * above for the sign — which `contrast.test.ts` asserts. **Colour is never the only
 * cue**: red and green are the pair most often confused, so every changed line also
 * carries a sign and a removed one is struck through, and a reader who sees neither
 * hue loses nothing.
 */
export const diff = {
  addedSurface: { light: '#cfeedb', dark: '#1b4029' },
  removedSurface: { light: '#fbd5d8', dark: '#4b2125' },
} as const satisfies Record<string, ModeValue>;

/**
 * The five lifecycle stages, **one colour per stage per role** — see
 * `docs/adr/0001-one-colour-per-stage.md`.
 *
 * `fill` is a solid ground with white `text.onBrand` on it, and is
 * mode-invariant: white text does not care what page the fill sits on.
 * `onSurface` is ink drawn straight onto the page — a rule, a card's accent bar,
 * a literal text colour — so it takes the mode's own ground into account. Dark's
 * `fill` at `onSurface`'s job is what fails: `design`'s `#7d41c8` reads 2.78:1
 * against the dark raised surface.
 *
 * `#1a8573` and `#be5a00` appear in Figma only as raw hex, and are named here
 * because a value nobody can name is a value that drifts.
 */
export const stage = {
  delivery: { fill: DELIVERY_MAGENTA, onSurface: { light: DELIVERY_MAGENTA, dark: '#f07ac8' } },
  discovery: { fill: '#1a8573', onSurface: { light: '#1a8573', dark: '#52e0c8' } },
  design: { fill: DESIGN_PURPLE, onSurface: { light: DESIGN_PURPLE, dark: '#c6a2ff' } },
  build: { fill: BUILD_BLUE, onSurface: { light: BUILD_BLUE, dark: '#5aa6ff' } },
  // Run's Figma orange is `brandOrange` by coincidence, not by reference: an
  // environment colour and a stage colour mean different things.
  run: { fill: '#be5a00', onSurface: { light: '#be5a00', dark: '#ff9d4d' } },
} as const;

export type StageKey = keyof typeof stage;

/**
 * One colour per content type — the badge on a search result card.
 *
 * **Its own named group, not aliases of the stage palette.** The values used
 * to coincide with three stage colours; the meanings never did, so
 * `stage.build.fill` on a Method badge would have tied "this is a method" to
 * "this is in Build" and broken both the day either moves. Same argument as
 * `environment` below.
 *
 * **Flat, because light's ground is derived from the colour** — `mixWithWhite`
 * at 0.9 — **and each value has to clear AA against that derived ground**, not
 * just against white: a colour merely at the AA floor on white cannot clear it
 * on its own tint, since the ratio approaches the white one from below as the
 * tint lightens. The previous values read 4.51:1–4.90:1 on white and so only
 * 3.98:1–4.29:1 on their own badges; these are ~8% darker and read
 * 4.60:1–4.62:1 there, for 5.26:1–5.28:1 on white. Lightening the tint further
 * isn't the alternative — at a 0.95 mix the two worst still read 4.24–4.25 —
 * so the colour is the only thing with room to move.
 *
 * Dark's ground can't be derived the same way, so it is written out below.
 */
export const contentType = {
  Phase: '#187969',
  Process: '#ad5200',
  Method: '#1e6eba',
} as const;

/**
 * The same three badges in dark, as **authored pairs rather than a derivation**.
 * Mixing a legible dark text colour 90% toward a dark ground converges on the
 * ground, so there is nothing to derive; each colour and each ground was chosen
 * to clear 4.5:1 against the other *and* against `surface.page`, which
 * `contrast.test.ts` pins.
 */
export const contentTypeDark = {
  Phase: { colour: '#21a791', surface: '#1b3234' },
  Process: { colour: '#ec7000', surface: '#3a291e' },
  Method: { colour: '#509ce3', surface: '#223040' },
} as const;

/**
 * The colour each environment paints itself in — the login page's whole
 * background, and the logo's tint everywhere else. **A screenshot should say
 * where it came from without anyone reading a word.**
 *
 * **Named here rather than aliased from the stage palette**, so changing the
 * Discovery brand does not change staging.
 *
 * `polaris` and `staging` match Luna's, the other tool everyone signs into.
 * `production` is the brand red because it is also `action.primary`.
 * `development` reuses polaris because it runs against the polaris pool and
 * API, so it is looking at polaris data.
 */
export const environment = {
  development: PALETTE.brandOrange,
  polaris: PALETTE.brandOrange,
  staging: '#5ac328',
  production: PALETTE.brandRed,
} as const;

/**
 * The spacing scale, in pixels. **The number is the multiplier at a 4px base**,
 * which is why `space[3]` is 12 and not 24 — see `SPACING_BASE` in `theme.ts`.
 */
export const space = {
  3: 12,
  4: 16,
  6: 24,
  8: 32,
  12: 48,
} as const;

/** `full` is a pill: large enough that any height rounds to a capsule. */
export const radius = {
  md: 8,
  lg: 16,
  xl: 20,
  full: 999,
} as const;

/**
 * The type scale, in pixels. **These are the design's names, and they are one
 * step off MUI's variants** — `display` is MUI's `h1`, this `h1` is its `h2`.
 * The mapping table is in `theme.ts`; keeping the design's names here means a
 * Figma inspection reads straight across.
 */
export const fontSize = {
  label: 12,
  bodySm: 14,
  body: 16,
  h2: 20,
  h1: 24,
  display: 32,
} as const;

/**
 * Two shadows, and no more. `1` lifts a card off the page, `2` lifts something
 * off a card. Anything deeper is a modal, which MUI already has an answer for.
 */
export const elevation = {
  1: '0 1px 3px rgba(0, 0, 0, 0.06)',
  2: '0 2px 8px rgba(0, 0, 0, 0.08)',
} as const;

/**
 * Font stacks — Poppins for headings, Roboto for everything else, both
 * self-hosted from `public/fonts`.
 *
 * The fallbacks are load-bearing: `font-display: swap` means the first paint
 * uses them, so a stack ending in a bare `sans-serif` reflows visibly.
 */
const SYSTEM_STACK =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';

export const fontFamily = {
  heading: `Poppins, ${SYSTEM_STACK}`,
  body: `Roboto, ${SYSTEM_STACK}`,
  /** System-only: mono renders commands, which no brand font needs to carry. */
  mono: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
} as const;

/**
 * Responsive breakpoints, in pixels — three, not MUI's five.
 *
 * **Only layout reflows across these. The type scale never changes**, which is
 * the responsive spec rather than an omission.
 */
export const breakpoint = {
  mobile: 0,
  tablet: 768,
  desktop: 1024,
} as const;

/** The widest the content gets, centred, with the page padding outside it. */
export const MAX_CONTENT_WIDTH = 1320;

/** Page gutter per breakpoint. */
export const pagePadding = {
  mobile: 16,
  tablet: 32,
  desktop: 60,
} as const;

/** Gap between cards in a grid, per breakpoint. */
export const gridGap = {
  mobile: 12,
  tablet: 16,
  desktop: 20,
} as const;

/**
 * Vertical air between the major blocks of a page.
 *
 * **Its own progression, like `gridGap`, and not a spacing multiple.** 40 is
 * not on the 12 / 16 / 24 / 32 / 48 scale, so `theme.spacing(10)` would look
 * like a scale value and stop matching the design the moment the scale moved.
 */
export const sectionSpacing = {
  mobile: 24,
  tablet: 32,
  desktop: 40,
} as const;

/**
 * How many columns of cards a grid gets, per breakpoint.
 *
 * **Two shapes, because the L1 grid's columns are *stages*.** At tablet the
 * fourth stage wraps under the first, so it drops to three rather than
 * halving; a grid of peers halves.
 */
export const gridColumns = {
  stage: { mobile: 1, tablet: 3, desktop: 4 },
  card: { mobile: 1, tablet: 2, desktop: 3 },
} as const;
